/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { alpha } from '@mui/material/styles';

import { getCommandIntelligenceTokens } from '../../theme/commandIntelligenceTokens';

/**
 * @param {import('@mui/material').Theme & { validateMode?: Function }} theme
 * @param {{ isMobile?: boolean, isHorizontalDock?: boolean }} [options]
 */
export const getCommandIntelligenceWelcomeHeroStyles = (theme, { isMobile = false, isHorizontalDock = false } = {}) => {
  const tokens = getCommandIntelligenceTokens(theme);

  const titleGradient = theme.validateMode({
    light: 'linear-gradient(135deg, #5B8DEE 0%, #1e293b 50%, #2684FF 100%)',
    dark: 'linear-gradient(135deg, #7BA3F5 0%, #f8fafc 50%, #60a5fa 100%)',
  });

  if (isHorizontalDock) {
    return {
      root: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        minHeight: 0,
      },
      content: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        width: '100%',
        minWidth: 0,
      },
      leading: {
        flexShrink: 0,
        maxWidth: { xs: '36%', sm: '32%', md: '28%' },
        minWidth: 0,
      },
      title: {
        fontWeight: 700,
        fontSize: '1rem',
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
        background: titleGradient,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      subtitle: {
        color: tokens.textMuted,
        fontWeight: 400,
        fontSize: '0.75rem',
        lineHeight: 1.35,
        mt: 0.25,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      intentSlot: {
        flexShrink: 0,
        minWidth: 0,
        maxWidth: '42%',
      },
      promptsScroll: {
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        flex: 1,
        minWidth: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        pb: 0.25,
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
          height: 4,
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: 4,
          bgcolor: alpha(tokens.textMuted, 0.35),
        },
      },
      promptButton: {
        flexShrink: 0,
        justifyContent: 'flex-start',
        textTransform: 'none',
        borderRadius: tokens.radiusChip,
        px: 1.25,
        py: 0.5,
        fontWeight: 500,
        fontSize: '0.75rem',
        minHeight: 32,
        borderColor: tokens.borderSubtle,
        color: tokens.textSecondary,
        bgcolor: tokens.surfaceInput,
        whiteSpace: 'nowrap',
        lineHeight: 1.3,
        '&:hover': {
          borderColor: alpha(tokens.accentPrimary, 0.45),
          bgcolor: alpha(tokens.accentPrimary, 0.06),
        },
      },
      promptButtonFeatured: {
        borderColor: tokens.accentPrimary,
        color: tokens.accentPrimary,
        fontWeight: 600,
        bgcolor: alpha(tokens.accentPrimary, 0.08),
        '&:hover': {
          borderColor: tokens.accentPrimary,
          bgcolor: alpha(tokens.accentPrimary, 0.14),
        },
      },
      attachHint: {
        flexShrink: 0,
        fontStyle: 'italic',
        color: theme.palette.warning.main,
        fontSize: '0.72rem',
        lineHeight: 1.3,
        maxWidth: '28%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    };
  }

  return {
    root: {
      minHeight: '100%',
      display: 'flex',
      alignItems: isMobile ? 'flex-start' : 'center',
      justifyContent: 'center',
      py: isMobile ? 1.5 : 2,
      px: isMobile ? 0.5 : 1,
    },
    content: {
      width: '100%',
      maxWidth: '100%',
      mx: 'auto',
    },
    logoWrap: {
      display: 'flex',
      justifyContent: 'center',
      mb: isMobile ? 1.25 : 1.75,
    },
    titleWrapper: {
      position: 'relative',
      display: 'inline-block',
      mb: isMobile ? 0.75 : 1.25,
      overflow: 'hidden',
      width: '100%',
      textAlign: 'center',
    },
    title: {
      textAlign: 'center',
      fontWeight: 700,
      fontSize: isMobile ? '1.35rem' : { xs: '1.5rem', sm: '1.75rem' },
      lineHeight: 1.15,
      letterSpacing: '-0.03em',
      background: titleGradient,
      backgroundSize: '240% 100%',
      backgroundPosition: '120% 50%',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      animation: 'commandIntelligenceWelcomeTitleShimmer 12s ease-in-out infinite',
      '@keyframes commandIntelligenceWelcomeTitleShimmer': {
        '0%': { backgroundPosition: '120% 50%' },
        '14%': { backgroundPosition: '-20% 50%' },
        '100%': { backgroundPosition: '-20% 50%' },
      },
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
        backgroundPosition: '50% 50%',
      },
    },
    subtitle: {
      color: tokens.textMuted,
      fontWeight: 400,
      fontSize: isMobile ? '0.8125rem' : '0.9rem',
      lineHeight: 1.5,
      maxWidth: '100%',
      mx: 'auto',
      textAlign: 'center',
      mb: isMobile ? 1.5 : 1.75,
      px: isMobile ? 0.5 : 0,
    },
    intentSlot: {
      mb: 1.5,
    },
    promptsLabel: {
      textAlign: 'center',
      fontSize: isMobile ? '0.68rem' : '0.72rem',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: tokens.textMuted,
      mb: isMobile ? 0.75 : 1,
    },
    promptsGrid: isMobile
      ? {
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          width: '100%',
        }
      : {
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 0.75,
        },
    promptButton: {
      justifyContent: 'flex-start',
      textTransform: 'none',
      borderRadius: tokens.radiusChip,
      px: 1.25,
      py: 0.9,
      fontWeight: 500,
      fontSize: isMobile ? '0.8rem' : '0.78rem',
      minHeight: isMobile ? 44 : 'auto',
      borderColor: tokens.borderSubtle,
      color: tokens.textSecondary,
      bgcolor: tokens.surfaceInput,
      whiteSpace: 'normal',
      textAlign: 'left',
      lineHeight: 1.35,
      '&:hover': {
        borderColor: alpha(tokens.accentPrimary, 0.45),
        bgcolor: alpha(tokens.accentPrimary, 0.06),
      },
      ...(isMobile && {
        '&:active': {
          bgcolor: alpha(tokens.accentPrimary, 0.1),
          transform: 'scale(0.98)',
        },
        '@media (prefers-reduced-motion: reduce)': {
          '&:active': {
            transform: 'none',
          },
        },
      }),
    },
    promptButtonFeatured: {
      borderColor: tokens.accentPrimary,
      color: tokens.accentPrimary,
      fontWeight: 600,
      bgcolor: alpha(tokens.accentPrimary, 0.08),
      '&:hover': {
        borderColor: tokens.accentPrimary,
        bgcolor: alpha(tokens.accentPrimary, 0.14),
      },
    },
    attachHint: {
      display: 'block',
      mt: 1.5,
      textAlign: 'center',
      fontStyle: 'italic',
      color: theme.palette.warning.main,
      fontSize: '0.75rem',
    },
  };
};
